<template>
  <div class="PostToolbar form-bottom">
    <div class="form-bottom-left">
      <slot name="upload" />
      <button
        v-if="polls"
        class="bottom-left-button poll-icon button-unstyled"
        :class="{ toggled: pollOn }"
        type="button"
        :title="$t('polls.add_poll')"
        :aria-pressed="pollOn ? 'true' : 'false'"
        @click="$emit('poll')"
      >
        <FAIcon icon="poll-h" />
      </button>
      <button
        v-if="quotes"
        class="bottom-left-button quote-icon button-unstyled"
        :disabled="quoteDisabled"
        :class="{ toggled: quoteOn }"
        type="button"
        :title="$t('tool_tip.add_quote')"
        :aria-pressed="quoteOn ? 'true' : 'false'"
        @click="$emit('quote')"
      >
        <FAIcon icon="quote-right" />
      </button>
      <button
        v-if="cw"
        class="bottom-left-button cw-button button-unstyled"
        :class="{ toggled: cwOn }"
        type="button"
        :title="$t('post_status.content_warning_toggle')"
        :aria-label="$t('post_status.content_warning_toggle')"
        :aria-pressed="cwOn ? 'true' : 'false'"
        @click="$emit('cw')"
      >
        {{ $t('post_status.content_warning_short') }}
      </button>
      <button
        class="bottom-left-button emoji-icon button-unstyled"
        type="button"
        :title="$t('emoji.add_emoji')"
        @click="$emit('emoji')"
      >
        <FAIcon :icon="['far', 'face-smile-beam']" />
      </button>
    </div>
    <div class="form-bottom-right">
      <span
        v-if="charactersLeft != null"
        class="character-counter"
        :class="{ error: charactersLeft < 0 }"
      >{{ charactersLeft }}</span>
      <div class="btn-group post-button-group">
        <button
          class="btn button-default post-button -primary"
          :disabled="postDisabled"
          @click.stop.prevent="$emit('post', $event)"
        >
          {{ posting ? $t('post_status.posting') : scheduled ? $t('post_status.schedule_button') : $t('post_status.post') }}
        </button>
        <MenuPopover
          v-if="menuItems.length"
          class="more-post-actions"
          :normal-button="true"
          trigger="click"
          placement="top"
          :offset="{ y: 5 }"
          :bound-to="{ x: 'container' }"
          bound-to-selector=".composer-box"
          :label="$t('post_status.more_post_actions')"
          :items="menuItems"
        >
          <template #trigger>
            <FAIcon icon="chevron-down" />
          </template>
        </MenuPopover>
      </div>
    </div>
  </div>
</template>

<script>
import MenuPopover from 'src/components/menu_popover/menu_popover.vue'

// The last row of the post form: the tools on the left, the counter, Post
// and the menu beside it on the right. The form keeps all the state.
export default {
  components: { MenuPopover },
  props: {
    // which tools the form offers, and which are on
    polls: Boolean,
    pollOn: Boolean,
    quotes: Boolean,
    quoteOn: Boolean,
    quoteDisabled: Boolean,
    cw: Boolean,
    cwOn: Boolean,
    // characters left, or null to hide the counter
    charactersLeft: { type: Number, default: null },
    posting: Boolean,
    // the post waits for a set time: Post reads Schedule
    scheduled: Boolean,
    postDisabled: Boolean,
    // MenuPopover items for the menu beside Post; none hides it
    menuItems: { type: Array, default: () => [] },
  },
  emits: ['poll', 'quote', 'cw', 'emoji', 'post'],
}
</script>

<style lang="scss">
@use "../../mixins";

/* the last row of the box: tools on the left, counter and Post on the
   right */
.PostToolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5em;
  padding: 0.25em 0.5em 0.5em;
  line-height: normal;

  .form-bottom-left {
    display: flex;
    align-items: center;
    gap: 0.1em;
    flex: 0 0 auto;
  }

  .bottom-left-button {
    display: inline-grid;
    place-items: center;
    width: 1.9em;
    height: 1.9em;
    padding: 0;
    border-radius: var(--r-item);
    line-height: 1;

    &.toggled {
      --icon: var(--accent);

      color: var(--accent);
      background-color: color-mix(in oklab, var(--accent) 14%, transparent);
    }
  }

  .cw-button {
    font-size: 0.8em;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--icon);
  }

  .form-bottom-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.4em;
    flex: 1 1 auto;
    min-width: 0;
  }

  .more-post-actions {
    height: 100%;

    .btn {
      height: 100%;
    }
  }

  .post-button-group {
    display: flex;

    .post-button {
      padding: 0 0.8em;
      font-weight: 600;
      border-start-end-radius: 0;
      border-end-end-radius: 0;
    }

    .more-post-actions > .popover-trigger-button {
      height: 100%;
      padding: 0 0.4em;
      border-start-start-radius: 0;
      border-end-start-radius: 0;
      margin-inline-start: -1px;
    }

    /* the menu button joins an active Post button's colour */
    .post-button:not(:disabled) + .more-post-actions > .popover-trigger-button {
      --icon: var(--onAccent);

      background-color: var(--accent);
      color: var(--onAccent);
      box-shadow: inset 1px 0 0 color-mix(in oklab, var(--onAccent) 25%, transparent);
    }

    /* the Post button alone, when there is no menu beside it */
    .post-button:last-child {
      border-radius: var(--roundness);
    }
  }

  .character-counter {
    font-size: var(--fs-sm);
    color: var(--textFaint);
    font-variant-numeric: tabular-nums;

    &.error {
      color: var(--cRed);
      font-weight: 600;
    }
  }
}

/* phones and touch screens: targets about 44px, easy to hit with a
   finger (sized in rem, so the smaller CW text does not shrink its box) */
@include mixins.touch {
  .PostToolbar {
    .form-bottom-left {
      gap: 0.25rem;
    }

    .bottom-left-button {
      width: mixins.$tap-size;
      height: mixins.$tap-size;
      border-radius: 50%;
    }

    .post-button-group {
      min-height: mixins.$tap-size;
    }

    .post-button,
    .more-post-actions > .popover-trigger-button {
      min-height: mixins.$tap-size;
    }

    .more-post-actions > .popover-trigger-button {
      min-width: mixins.$tap-size;
    }
  }
}
</style>
